import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RFQ } from '../../../../types/rfq';
import { EmailAttachment, EmailParams } from '../../../../types/email';
import { CustomerProfile } from '../../../../utils/customerRules';
import { toast } from '../../../../utils/notifications';
import { sendEmail } from '../../../../services/emailService';
import { isSendOutcomeUnknown } from '../../../../services/sendOutcome';
import { useUser } from '../../../../contexts/UserContext';
import { logSentQuoteItems, updateRFQ } from '../../../../api/client';
import { convertToHtml } from '../emailTemplates';
import { SavedRemarkFilePreview } from './useQuoteAttachments';
import { queryKeys } from "../../../../api/queryKeys";
import type { ErrorLike } from "../../../../utils/errors";

/** The comma-separated CC field, reduced to the well-formed addresses in it. */
const validateEmails = (emails: string): string[] => {
  if (!emails.trim()) return [];
  return emails.split(',')
    .map(email => email.trim())
    .filter(email => email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/));
};

/** Everything the send needs, as the dialog holds it at the moment Send is clicked. */
interface QuoteDraft {
  rfq: RFQ;
  profile: CustomerProfile;
  originalSubject: string | null;
  subject: string;
  emailContent: string;
  ccEmails: string;
  fileAttachments: EmailAttachment[];
  savedRemarkFiles: SavedRemarkFilePreview[];
  excludedSavedRemarkFileIds: Set<number>;
  attachPdf: boolean;
  pdfData: string | null;
  /** The quote PDF is being generated. */
  isPdfLoading: boolean;
  /** The customer's Excel is being filled, or its auto-run is about to start. */
  isExcelBusy: boolean;
}

/**
 * Why the quote cannot go out yet, or null when it can. Sending while the PDF
 * or the customer's Excel is still being built sent the quote without it and
 * reported success - and Seven Seas' procedure requires their filled Excel.
 */
function sendBlockedReason(
  draft: Pick<QuoteDraft, 'attachPdf' | 'pdfData' | 'isPdfLoading' | 'isExcelBusy'>,
): string | null {
  if (draft.isExcelBusy) {
    return "The customer's Excel is still being filled with the prices.";
  }
  if (draft.isPdfLoading) {
    return 'The quote PDF is still being generated.';
  }
  if (draft.attachPdf && !draft.pdfData) {
    return 'The quote PDF is not ready. Untick "Attach Quote PDF" or wait for it.';
  }
  return null;
}

/** The email the quote goes out as: body, CC, threading headers and attachments. */
function buildEmailParams(p: {
  rfq: RFQ;
  subject: string;
  emailContent: string;
  senderName: string;
  excludeSavedRemarkFileIds: number[];
  ccEmails: string;
  originalSubject: string | null;
  attachments: EmailAttachment[];
  /** The quote PDF, when it is to be attached. */
  pdfData: string | null;
}): EmailParams {
  const { rfq } = p;
  const emailParams: EmailParams = {
    to: [rfq.client_email],
    subject: p.subject,
    body: convertToHtml(p.emailContent),
    isHtml: true,
    rfqId: rfq.rfq_id,
    senderName: p.senderName,
    excludeSavedRemarkFileIds: p.excludeSavedRemarkFileIds
  };

  const validCCs = validateEmails(p.ccEmails);
  if (validCCs.length > 0) {
    emailParams.cc = validCCs;
  }

  // In-Reply-To and References headers for email threading
  if (rfq.original_email_id) {
    emailParams.inReplyTo = rfq.original_email_id;
    if (rfq.email_references) {
      emailParams.references = rfq.email_references;
    }
  }

  // The original subject, UID and sender, for matching the reply to the RFQ
  if (p.originalSubject) {
    emailParams.originalSubject = p.originalSubject;
  }
  if (rfq.original_email_uid) {
    emailParams.originalEmailUid = rfq.original_email_uid;
  }
  emailParams.originalSender = rfq.client_email;

  if (p.attachments.length > 0) {
    emailParams.attachments = p.attachments;
  }

  if (p.pdfData) {
    const pdfAttachment = {
      name: `Quote_${rfq.rfq_id}.pdf`,
      contentType: 'application/pdf',
      content: p.pdfData
    };

    if (emailParams.attachments) {
      emailParams.attachments.push(pdfAttachment);
    } else {
      emailParams.attachments = [pdfAttachment];
    }
  }

  return emailParams;
}

/** What a failed send tells the user. */
function sendErrorDescription(errorMsg: string, statusCode: number | '', serverError: string): string {
  if (errorMsg.includes('network') || !navigator.onLine) {
    return 'Network connection issue. Please check your internet connection and try again.';
  }
  if (statusCode === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (typeof statusCode === 'number' && statusCode >= 500) {
    return 'Server error. Our team has been notified and is working on a fix.';
  }
  if (errorMsg.includes('authentication') || statusCode === 401) {
    return 'Authentication error. Please try logging out and back in.';
  }
  return `${errorMsg || serverError || 'Unknown error'}${statusCode ? ` (Status: ${statusCode})` : ''}`;
}

/**
 * Sending the quote: mark the RFQ QUOTED, send, log the sent items, and on a
 * failed send put the RFQ back to NEW. The dialog closes the moment Send is
 * clicked, so everything sent is captured from the draft before that.
 *
 * A send whose outcome is unknown (timeout, dropped connection, gateway
 * error) is NOT a failure: the backend may have sent it, so the RFQ stays
 * QUOTED and staff are told to check the Sent folder before resending.
 */
export function useSendQuote(draft: QuoteDraft, onClose: () => void) {
  const queryClient = useQueryClient();
  const { selectedUser } = useUser();
  // True from the click until the send settles. The ref is the guard (two
  // clicks can land before a re-render); the state disables the controls if
  // the dialog is reopened while the send is still in flight.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const blockedReason = sendBlockedReason(draft);

  const handleSendEmail = async () => {
    if (submittingRef.current) return;
    if (blockedReason) {
      toast({
        title: "Not Ready to Send",
        description: blockedReason,
        severity: "warning"
      });
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await sendQuote();
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const sendQuote = async () => {
    const { rfq, profile, originalSubject, ccEmails, savedRemarkFiles } = draft;
    // IMPORTANT: Capture current state BEFORE closing dialog
    // This prevents the useEffect from clearing attachments before they're used
    const currentAttachments = [...draft.fileAttachments];
    // Saved remark files are attached by the backend at send time; all it needs
    // from here is which ones the user dropped. Seven Seas gets none at all -
    // their portal requires drawings/datasheets to go to the user separately.
    const currentExcludedRemarkFileIds = profile.excludeRemarkFiles
      ? savedRemarkFiles.map(f => f.id)
      : Array.from(draft.excludedSavedRemarkFileIds);
    const currentSubject = draft.subject;
    const currentEmailContent = draft.emailContent;
    const currentAttachPdf = draft.attachPdf;
    const currentPdfData = draft.pdfData;

    // Close dialog immediately to improve UX
    onClose();

    // Show initial toast
    toast({
      title: "Preparing Email",
      description: "Your quote email is being prepared...",
      severity: "info"
    });

    const senderName = selectedUser?.name || "System";
    const timestamp = new Date().toISOString();
    // Set just before sendEmail: an error before it means nothing was sent.
    let sendAttempted = false;

    try {
      // First update RFQ status to QUOTED
      await updateRFQ(rfq.rfq_id, {
        status: "QUOTED",
        quote_sent_by: senderName,
        quote_sent_at: timestamp
      });

      // Immediately invalidate queries to update UI
      queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.rfq(rfq.rfq_id) });

      const emailParams = buildEmailParams({
        rfq,
        subject: currentSubject,
        emailContent: currentEmailContent,
        senderName,
        excludeSavedRemarkFileIds: currentExcludedRemarkFileIds,
        ccEmails,
        originalSubject,
        attachments: currentAttachments,
        pdfData: currentAttachPdf ? currentPdfData : null,
      });

      // Send the email directly instead of using mutation (to use captured values)
      const totalAttachmentCount =
        (emailParams.attachments?.length || 0) +
        savedRemarkFiles.filter(f => !currentExcludedRemarkFileIds.includes(f.id)).length;

      sendAttempted = true;
      await sendEmail(emailParams);

      // Log the sent quote items. The email has already gone out, so a
      // logging failure must not reach the catch below: that would tell the
      // user the send failed and put the RFQ back to NEW, inviting a second,
      // duplicate quote.
      const rfqWithSender = {
        ...rfq,
        quote_sent_by: senderName,
        quote_sent_at: timestamp
      };

      try {
        await logSentQuoteItems(
          rfqWithSender,
          currentAttachPdf ? `Quote_${rfq.rfq_id}.pdf` : ''
        );
      } catch (logError) {
        console.error('Quote email sent, but logging the sent items failed:', logError);
        toast({
          title: "Quote sent, history not recorded",
          description: "The email went out, but its items could not be logged to quote history. Do not resend.",
          severity: "warning"
        });
      }

      toast({
        title: "Email Sent Successfully",
        description: `Your quote has been sent${totalAttachmentCount > 0 ? ` with ${totalAttachmentCount} attachment(s)` : ''}`,
        severity: "success"
      });

    } catch (caught) {
      const error = caught as ErrorLike;
      // Extract error details
      const errorMsg = error.message || '';
      const statusCode = error.response?.status || '';
      const serverError = error.response?.data?.error || '';

      if (sendAttempted && isSendOutcomeUnknown(error)) {
        // No answer is not a failed send: the backend may have sent it.
        // Reverting to NEW here invited a second, duplicate quote.
        queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.rfq(rfq.rfq_id) });
        toast({
          title: "Send Status Unknown",
          description: `${errorMsg || 'No reply from the server'}. The email may have been sent - check the Sent folder before resending. RFQ #${rfq.rfq_id} has been left as QUOTED.`,
          severity: "warning"
        });
        return;
      }

      // Revert RFQ status to NEW since email failed. A failed revert must not
      // swallow the error toast: the user still has to learn the send failed.
      let revertFailed = false;
      try {
        await updateRFQ(rfq.rfq_id, {
          status: "NEW",
          // null, not undefined: JSON drops undefined, which left the sender
          // and time of a send that never happened on the RFQ
          quote_sent_by: null,
          quote_sent_at: null,
          notes: `Email send failed at ${new Date().toISOString()} - ${errorMsg || serverError || 'Unknown error'}`
        });
      } catch (revertError) {
        console.error(`Email send failed, and reverting RFQ ${rfq.rfq_id} to NEW failed too:`, revertError);
        revertFailed = true;
      }

      // Re-invalidate queries to show the RFQ again
      queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.rfq(rfq.rfq_id) });

      const errorDescription = sendErrorDescription(errorMsg, statusCode, serverError);

      toast({
        title: "Email Error",
        description: `Failed to send email: ${errorDescription}${revertFailed
          ? ` - and RFQ #${rfq.rfq_id} could not be moved back to NEW and may still show as QUOTED.`
          : ''}`,
        severity: "error"
      });
    }
  };

  return { handleSendEmail, isSubmitting, blockedReason };
}
